/**
 * Full-screen single-day order entry — `/orders/school/day?locationId&date`.
 *
 * The mobile counterpart to expanding a day card in place: tapping a day on the
 * calendar navigates here, rendered as a full-screen sheet (over the app
 * chrome) per the Figma. The header is a compact date pill + weekday + a close
 * (X) button; the body is `DayOrderPanel` (location tabs → stacked mealtime
 * tables → "Add meals to cart"), the same UI the desktop in-place expand
 * renders. Switching campus on the location tabs swaps the `locationId`
 * search param in place.
 */

import { Skeleton } from '@ordo/ui/skeleton'
import { X } from 'lucide-react'
import { type ReactElement, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { isChannelEnabled, useOrgOrderingConfigQuery } from '@/api/org-ordering-config'
import {
  type AdminSchool,
  type SchoolForecastBatchRow,
  useMySchoolAdminLocationsQuery,
  useSchoolForecastBatchesForMonthQuery,
} from '@/api/school-orders'
import { DayOrderPanel } from '@/components/orders/school/day-order-panel'
import { buildAlreadyOrdered } from '@/lib/school-orders/already-ordered'
import { dateFromKey } from '@/lib/school-orders/calendar'
import { bandAudienceFold } from '@/lib/school-orders/columns'
import { acceptsSchoolOrders } from '@/lib/school-orders/orderable-campuses'
import { resolveSelectedSchool } from '@/lib/school-orders-selection'

/** Full-screen sheet shell — sits above the app chrome. */
function Sheet({ children }: { children: ReactElement }): ReactElement {
  return <div className="fixed inset-0 z-50 flex flex-col bg-background">{children}</div>
}

/** Compact header: date pill + weekday + close (X). */
function DayHeader({ dateKey, onClose }: { dateKey: string; onClose: () => void }): ReactElement {
  const d = dateFromKey(dateKey)
  const monthAbbr = d.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' }).toUpperCase()
  const dayNum = d.getUTCDate()
  const weekday = d.toLocaleDateString(undefined, { weekday: 'long', timeZone: 'UTC' })
  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-md bg-muted leading-none">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{monthAbbr}</span>
          <span className="text-base font-semibold text-foreground">{dayNum}</span>
        </span>
        <h1 className="text-2xl font-medium tracking-tight text-foreground">{weekday}</h1>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground">
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

export function SchoolDayPage(): ReactElement {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const requestedLocationId = params.get('locationId')
  const date = params.get('date') ?? ''
  const schoolsQuery = useMySchoolAdminLocationsQuery()
  const schools = useMemo<AdminSchool[]>(() => schoolsQuery.data?.me?.schoolAdminLocations ?? [], [schoolsQuery.data])
  const school = useMemo(() => resolveSelectedSchool(schools, requestedLocationId), [schools, requestedLocationId])

  const close = (): void => {
    navigate(
      requestedLocationId ? `/orders/school?locationId=${encodeURIComponent(requestedLocationId)}` : '/orders/school',
    )
  }

  if (schoolsQuery.isPending && !schoolsQuery.data) {
    return (
      <Sheet>
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-10 w-40" />
          <Skeleton className="mt-2 h-64 w-full" />
        </div>
      </Sheet>
    )
  }
  if (!school || !date) {
    return (
      <Sheet>
        <div className="flex h-full flex-col">
          <DayHeader dateKey={date || '1970-01-01'} onClose={close} />
          <p className="p-4 text-sm text-muted-foreground">Day not found.</p>
        </div>
      </Sheet>
    )
  }
  const selectSchool = (locationId: string): void => {
    navigate(`/orders/school/day?locationId=${encodeURIComponent(locationId)}&date=${encodeURIComponent(date)}`, {
      replace: true,
    })
  }
  return <DayBody school={school} schools={schools} date={date} onClose={close} onSelectSchool={selectSchool} />
}

function DayBody({
  school,
  schools,
  date,
  onClose,
  onSelectSchool,
}: {
  school: AdminSchool
  schools: AdminSchool[]
  date: string
  onClose: () => void
  onSelectSchool: (locationId: string) => void
}): ReactElement {
  const d = dateFromKey(date)
  const batches = useSchoolForecastBatchesForMonthQuery({
    schoolLocationId: school.id,
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
  })
  const rows = useMemo<SchoolForecastBatchRow[]>(
    () => batches.data?.schoolForecastBatchesForMonth ?? [],
    [batches.data],
  )
  const familyRows = useMemo(() => batches.data?.familyOrders ?? [], [batches.data])
  const alreadyOrdered = useMemo(
    () =>
      buildAlreadyOrdered(rows, {
        wholeSchool: school.orderGroupBy === 'NONE',
        familyOrders: familyRows,
        // Fold campus-tier audiences into the locked band columns.
        foldTierAudience: bandAudienceFold(school),
      }),
    [rows, familyRows, school],
  )

  const orgConfig = useOrgOrderingConfigQuery(school.organizationId)
  // Same two read-only reasons as the week view (`SchoolOrdersBody`): the
  // org's SCHOOL_ORDER channel is off, or this campus is PARENT-only.
  const readOnly = !isChannelEnabled(orgConfig.data ?? null, 'SCHOOL_ORDER') || !acceptsSchoolOrders(school)

  return (
    <Sheet>
      <div className="flex h-full min-h-0 flex-col">
        <DayHeader dateKey={date} onClose={onClose} />
        <div className="flex min-h-0 flex-1 flex-col">
          <DayOrderPanel
            school={school}
            dateKey={date}
            alreadyOrdered={alreadyOrdered}
            readOnly={readOnly}
            schools={schools}
            onSelectSchool={onSelectSchool}
          />
        </div>
      </div>
    </Sheet>
  )
}
